import { useEffect } from 'react';

import styles from './Modal.module.scss';

/**
 * Modal 컴포넌트
 * - 범용 모달 컴포넌트
 * - ESC 키로 닫기, 배경 클릭으로 닫기 지원
 *
 * @param {boolean} isOpen - 모달 표시 여부
 * @param {Function} onClose - 닫기 핸들러
 * @param {string} title - 모달 제목 (선택)
 * @param {ReactNode} children - 모달 내용
 * @param {string} className - 추가 CSS 클래스
 * @param {string} size - 모달 크기 ('small', 'medium', 'large', 'fullscreen')
 * @param {boolean} showCloseButton - 닫기 버튼 표시 여부 (기본: false)
 * @param {boolean} closeOnOverlayClick - 배경 클릭 시 닫기 여부 (기본: true)
 */
function Modal({
  isOpen,
  onClose,
  title,
  children,
  className = '',
  size = 'medium',
  showCloseButton = false,
  closeOnOverlayClick = true,
  classes = {},
}) {
  // ESC 키로 모달 닫기
  useEffect(
    function () {
      if (!isOpen) {
        return;
      }

      function handleEscapeKey(e) {
        if (e.key === 'Escape') {
          onClose();
        }
      }

      document.addEventListener('keydown', handleEscapeKey);
      // body 스크롤 방지
      document.body.style.overflow = 'hidden';

      return function () {
        document.removeEventListener('keydown', handleEscapeKey);
        document.body.style.overflow = '';
      };
    },
    [isOpen, onClose],
  );

  // 모달이 닫혀있으면 렌더링하지 않음
  if (!isOpen) {
    return null;
  }

  // 배경 클릭 핸들러
  function handleOverlayClick(e) {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  }

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div
        className={[
          styles.modalContent,
          styles[size],
          classes.modalContent, // 외부에서 override 가능
        ].join(' ')}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || showCloseButton) && (
          <div className={[styles.modalHeader, classes.modalHeader].join(' ')}>
            {title && (
              <h2 className={[styles.modalTitle, classes.modalTitle].join(' ')}>{title}</h2>
            )}
            {showCloseButton && (
              <button
                type="button"
                className={[styles.closeButton, classes.closeButton].join(' ')}
                onClick={onClose}
                aria-label="닫기"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="25"
                  height="25"
                  viewBox="0 0 25 25"
                  fill="none"
                >
                  <path d="M18.75 6.25L6.25 18.75M6.25 6.25L18.75 18.75" stroke="#212121" />
                </svg>
              </button>
            )}
          </div>
        )}
        <div className={[styles.modalBody, classes.modalBody].join(' ')}>{children}</div>
      </div>
    </div>
  );
}

export default Modal;
